import React, { useMemo } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { type CrossClientQueryResult } from '../../../models/CrossClient'
import { type ButtonVariant } from '../../../components/Button/Button'
import { ActionsToggle, CrossClientActionsDropdown, type CrmAction, type CrmActionsMap } from '../Actions/CrossClientActionsDropdown'
import { CRM_ACTION_REGISTRY, type CrmActionKind } from '../Actions/crmActionRegistry'
import { useCrmActionHost } from '../Actions/useCrmActionHost'

interface CrossClientChooseActionDropdownProps {
  count: number
  resolveTargets: () => Promise<CrossClientQueryResult[]>
  label?: string
  toggleVariant?: ButtonVariant
  toggleClassName?: string
}

export const CrossClientChooseActionDropdown: React.FC<CrossClientChooseActionDropdownProps> = ({
  count,
  resolveTargets,
  label = 'Choose action',
  toggleVariant = 'primary',
  toggleClassName = 'button-medium rounded-3 text-nowrap',
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser } = useGetMeQuery({ accountId })
  const { open } = useCrmActionHost()

  const crmActions = useMemo<CrmActionsMap>(() => {
    const connectedServiceIds = new Set((currentUser?.integrations ?? []).map((integration) => integration.serviceId))

    const map: CrmActionsMap = {}
    for (const [kind, descriptor] of Object.entries(CRM_ACTION_REGISTRY) as [CrmActionKind, (typeof CRM_ACTION_REGISTRY)[CrmActionKind]][]) {
      if (!connectedServiceIds.has(descriptor.serviceId)) {
        continue
      }
      const action: CrmAction = {
        label: descriptor.label,
        icon: descriptor.icon,
        onClick: () => open(kind, { resolveResults: resolveTargets }),
      }
      const existing = map[descriptor.serviceId] ?? []
      map[descriptor.serviceId] = [...existing, action]
    }
    return map
  }, [currentUser?.integrations, open, resolveTargets])

  return (
    <CrossClientActionsDropdown
      count={count}
      crmActions={crmActions}
      toggle={
        <ActionsToggle variant={toggleVariant} className={toggleClassName}>
          {label}
          <i className='bi bi-chevron-expand ms-1' />
        </ActionsToggle>
      }
    />
  )
}
